/** Record the human review of a client MCP diagnose run (`console_entity_ai`; backend WPS P4-03).
 *
 *  `OK` — the answer the client got, and the judge's call on it, were right; `Problem` — either was
 *  wrong (a released answer that leaks or misleads, a withheld one that was fine). An optional
 *  note (at most 500 characters) says what. A later review replaces this one; the console audit
 *  log keeps each (`mcp_diagnose.reviewed`). Any client run may be reviewed — an unflagged one is
 *  recorded as reviewed by hand. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  mcpDiagnoseRunKeys,
  recordReview,
  REVIEW_NOTE_MAX,
  reviewErrorText,
  type McpDiagnoseRunDetail,
} from '@/api/mcpDiagnoseRuns';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

const FormSchema = z.object({
  outcome: z.enum(['ok', 'problem'], { message: 'Say whether the answer and the judge’s call were right.' }),
  note: z.string().trim().max(REVIEW_NOTE_MAX, `At most ${REVIEW_NOTE_MAX} characters.`),
});
type FormValues = z.infer<typeof FormSchema>;

export function ReviewDialog({
  run,
  onClose,
}: {
  run: McpDiagnoseRunDetail;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      outcome: (run.review?.outcome ?? undefined) as FormValues['outcome'],
      note: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => recordReview(run.task_id, { outcome: v.outcome, note: v.note.trim() }),
    onSuccess: (next) => {
      queryClient.setQueryData(mcpDiagnoseRunKeys.detail(run.task_id), next);
      void queryClient.invalidateQueries({ queryKey: mcpDiagnoseRunKeys.list });
      snackbar.show('Review recorded.', 'success');
      onClose();
    },
  });

  return (
    <AppDialog
      open
      onClose={onClose}
      size="sm"
      disableBackdropClose
      title="Record the review"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={mutation.isPending}
            onClick={handleSubmit((v) => mutation.mutate(v))}
          >
            {mutation.isPending ? 'Saving…' : 'Record review'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {run.review?.state === 'reviewed'
            ? 'This run was reviewed already: a new review replaces it (the audit log keeps both).'
            : run.review
              ? 'Flagged for review. Read the question, what the client got and what the judge withheld, then record what you found.'
              : 'Not flagged: recording a review marks it reviewed by hand.'}
        </Typography>
        <Controller
          control={control}
          name="outcome"
          render={({ field }) => (
            <FormControl error={Boolean(errors.outcome)}>
              <FormLabel id="review-outcome">Outcome</FormLabel>
              <RadioGroup
                aria-labelledby="review-outcome"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <FormControlLabel
                  value="ok"
                  control={<Radio size="small" />}
                  label="OK — the answer, and the judge’s call on it, were right"
                />
                <FormControlLabel
                  value="problem"
                  control={<Radio size="small" />}
                  label="Problem — the answer or the judge’s call was wrong"
                />
              </RadioGroup>
              {errors.outcome && <FormHelperText>{errors.outcome.message}</FormHelperText>}
            </FormControl>
          )}
        />
        <TextField
          {...register('note')}
          size="small"
          label="Note (optional)"
          multiline
          minRows={2}
          inputProps={{ maxLength: REVIEW_NOTE_MAX }}
          error={Boolean(errors.note)}
          helperText={
            errors.note?.message ??
            'What was wrong, or why it is fine. Kept on the run and in the console audit log; never shown to the client.'
          }
        />
        {mutation.isError && <Alert severity="error">{reviewErrorText(mutation.error)}</Alert>}
      </Stack>
    </AppDialog>
  );
}
